import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Button, TextField, Container, Paper, Chip, Divider, Fade, IconButton, CircularProgress } from '@mui/material';
import { styled } from '@mui/system';
import LockIcon from '@mui/icons-material/Lock';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { useAuth } from './context/AuthContext';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const CREDIT_PRICE_CENTS = parseInt(process.env.REACT_APP_CREDIT_PRICE_CENTS, 10);

// --- STYLED COMPONENTS (Unchanged) ---
const Root = styled('div')({
    backgroundColor: '#1c1c1c',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    color: '#fff',
});

const MainContent = styled(Container)({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '4rem 1rem',
});

const PlanCard = styled(Paper)(({ isSelected }) => ({
    backgroundColor: '#202020',
    padding: '2rem',
    borderRadius: '12px',
    border: isSelected ? '2px solid #12c099' : '2px solid #333',
    transition: 'border 0.3s ease-in-out',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
}));

const PaymentContainer = styled(Paper)({
    backgroundColor: '#202020',
    padding: '2.5rem',
    borderRadius: '12px',
    height: '100%',
});

const Footer = styled(Box)({
    width: '100%',
    padding: '2rem 0',
    textAlign: 'center',
    fontSize: '0.9rem',
    color: '#b0b0b0',
    borderTop: '1px solid #303030',
    backgroundColor: '#141414',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
});

const plans = [
    {
        label: 'Buy More Credits',
        priceId: 'pay_as_you_go',
        type: 'One-Time Purchase',
        description: 'Perfect for your next workout without any commitment.',
    },
    {
        label: '2x/Month',
        price: 150,
        priceDisplay: '$150 / month',
        credits: '4 Credits',
        priceId: process.env.REACT_APP_STRIPE_PRICE_ID_150,
        type: 'Subscription',
        description: 'Ideal for consistent training with 2 sessions per month.',
    },
    {
        label: '4x/Month',
        price: 300,
        priceDisplay: '$300 / month',
        credits: '8 Credits',
        priceId: process.env.REACT_APP_STRIPE_PRICE_ID_300,
        type: 'Subscription',
        description: 'The ultimate value for dedicated athletes with 4 sessions per month.',
        bestValue: true,
    },
];

// --- The Checkout Form Component ---
const CheckoutForm = ({ selectedPlan, creditAmount }) => {
    const stripe = useStripe();
    const elements = useElements();
    const { currentUser } = useAuth();
    const [nameOnCard, setNameOnCard] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!stripe || !elements || !currentUser) return;

        setIsLoading(true);
        setError(null);

        const cardElement = elements.getElement(CardElement);
        const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: { name: nameOnCard },
        });

        if (paymentMethodError) {
            setError(paymentMethodError.message);
            setIsLoading(false);
            return;
        }

        try {
            if (selectedPlan.type === 'Subscription') {
                await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/stripe/create-subscription`, {
                    firebaseUid: currentUser.uid,
                    priceId: selectedPlan.priceId,
                    paymentMethodId: paymentMethod.id,
                });
            } else {
                const totalAmountCents = creditAmount * CREDIT_PRICE_CENTS;
                const { data: intent } = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/stripe/create-payment-intent`, {
                    customerId: currentUser.stripeCustomerId,
                    amount: totalAmountCents,
                });
                const { error: confirmError } = await stripe.confirmCardPayment(intent.client_secret, {
                    payment_method: paymentMethod.id
                });
                if (confirmError) throw new Error(confirmError.message);
                await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/update-credits`, {
                    firebaseUid: currentUser.uid,
                    credits: creditAmount,
                    amount: totalAmountCents,
                    transactionId: intent.id,
                    status: 'succeeded',
                    url: intent.charges?.data?.[0]?.receipt_url || '',
                });
            }
            setSuccess(true);
        } catch (err) {
            console.error("Payment failed:", err);
            setError(err.message || "An unexpected error occurred.");
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <Box textAlign="center">
                <Typography variant="h5" color="#12c099">Payment Successful!</Typography>
                <Typography sx={{ color: 'grey.300' }}>Your purchase is complete. Welcome aboard!</Typography>
            </Box>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            <Typography fontWeight="bold" sx={{ mb: 1, color: 'grey.100' }}>Payment Information</Typography>
            <Box sx={{ border: '1px solid #444', borderRadius: 1, p: 1.5, mb: 2, backgroundColor: '#1c1c1c' }}>
                <CardElement options={{ style: { base: { iconColor: '#fff', color: '#fff', '::placeholder': { color: '#a0a0a0' } } } }} />
            </Box>

            <TextField
                fullWidth
                label="Name on Card"
                variant="filled"
                value={nameOnCard}
                onChange={(e) => setNameOnCard(e.target.value)}
                InputLabelProps={{ style: { color: '#a0a0a0' } }}
                sx={{ input: { color: '#fff' }, mb: 2, backgroundColor: '#333' }}
            />

            <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={!stripe || isLoading}
                sx={{ backgroundColor: '#12c099', color: '#fff', fontWeight: 'bold', py: 1.5, fontSize: '1rem', '&:hover': { backgroundColor: '#10a088' } }}
            >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Confirm Purchase'}
            </Button>
            {error && <Typography color="error" sx={{ mt: 2, textAlign: 'center' }}>{error}</Typography>}
            <Box display="flex" alignItems="center" justifyContent="center" mt={2} sx={{ color: 'grey.500' }}>
                <LockIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="caption">Secure SSL Checkout</Typography>
            </Box>
        </form>
    );
};


const PaymentPage = () => {
    const [selectedPlan, setSelectedPlan] = useState(plans[2]);
    const [creditAmount, setCreditAmount] = useState(1);

    const handleCreditChange = (amount) => {
        const newAmount = creditAmount + amount;
        if (newAmount >= 1) {
            setCreditAmount(newAmount);
        }
    };

    const totalPayAsYouGoPrice = (creditAmount * CREDIT_PRICE_CENTS / 100).toFixed(2);

    return (
        <Root>
            <MainContent maxWidth="lg">
                <Box textAlign="center" mb={5}>
                    <Typography variant="h4" sx={{ color: 'grey.300' }}>
                        Continue your journey with a plan that works for you.
                    </Typography>
                </Box>
                <Grid container spacing={5}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, color: 'grey.100' }}>
                            1. Choose Your Plan
                        </Typography>
                        <Grid container spacing={2}>
                            {plans.map((plan) => (
                                <Grid item xs={12} key={plan.priceId}>
                                    <PlanCard isSelected={selectedPlan?.priceId === plan.priceId} onClick={() => setSelectedPlan(plan)}>
                                        {plan.bestValue && (<Chip label="Best Value" size="small" sx={{ backgroundColor: '#12c099', color: '#fff', fontWeight: 'bold', position: 'absolute', top: 12, right: 12 }}/>)}
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <Box>
                                                <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff' }}>{plan.label}</Typography>
                                                <Typography variant="body2" sx={{ color: 'grey.400' }}>
                                                    {plan.priceId === 'pay_as_you_go' ? `${creditAmount} Credits` : plan.credits}
                                                </Typography>
                                            </Box>
                                            {/* ✅ FIX: Dollar amount color is now white */}
                                            <Typography variant="h5" fontWeight="bold" sx={{ color: '#fff' }}>
                                                {plan.priceId === 'pay_as_you_go' ? `$${totalPayAsYouGoPrice}` : plan.priceDisplay}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ mt: 2, color: 'grey.300' }}>{plan.description}</Typography>
                                        {plan.priceId === 'pay_as_you_go' && selectedPlan?.priceId === plan.priceId && (
                                            <Box display="flex" alignItems="center" justifyContent="center" mt={2}>
                                                <IconButton onClick={() => handleCreditChange(-1)}><RemoveCircleOutlineIcon sx={{ color: '#12c099' }}/></IconButton>
                                                <Typography variant="h6" mx={2}>{creditAmount}</Typography>
                                                <IconButton onClick={() => handleCreditChange(1)}><AddCircleOutlineIcon sx={{ color: '#12c099' }}/></IconButton>
                                            </Box>
                                        )}
                                    </PlanCard>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" fontWeight="bold" sx={{ mb: 3, color: 'grey.100' }}>
                            2. Complete Payment
                        </Typography>
                        <Fade in={Boolean(selectedPlan)} timeout={500}>
                            <PaymentContainer>
                                <Box p={2} borderRadius={2} sx={{ backgroundColor: '#333', mb: 3 }}>
                                    <Typography fontWeight="bold" gutterBottom sx={{color: 'grey.100'}}>Order Summary</Typography>
                                    <Box display="flex" justifyContent="space-between" mb={1}>
                                        <Typography sx={{color: 'grey.300'}}>{selectedPlan?.label} ({selectedPlan?.type})</Typography>
                                        {/* ✅ FIX: Dollar amount color is now white */}
                                        <Typography fontWeight="bold" sx={{color: '#fff'}}>${selectedPlan.priceId === 'pay_as_you_go' ? totalPayAsYouGoPrice : selectedPlan.price.toFixed(2)}</Typography>
                                    </Box>
                                    <Divider sx={{ borderColor: 'grey.700', my: 1 }} />
                                    <Box display="flex" justifyContent="space-between" mt={1}>
                                        <Typography variant="h6" fontWeight="bold" sx={{color: 'grey.100'}}>Total Due Today</Typography>
                                        {/* ✅ FIX: Dollar amount color is now white */}
                                        <Typography variant="h6" fontWeight="bold" sx={{color: '#fff'}}>${selectedPlan.priceId === 'pay_as_you_go' ? totalPayAsYouGoPrice : selectedPlan.price.toFixed(2)}</Typography>
                                    </Box>
                                </Box>
                                <Elements stripe={stripePromise}>
                                    <CheckoutForm selectedPlan={selectedPlan} creditAmount={creditAmount} />
                                </Elements>
                            </PaymentContainer>
                        </Fade>
                    </Grid>
                </Grid>
            </MainContent>
            <Footer>
                <Typography variant="body2">
                    © 2025 United Artificial Intelligence Company, Inc. All rights reserved.
                </Typography>
            </Footer>
        </Root>
    );
};

export default PaymentPage;